import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { Link, useLocation } from 'react-router-dom';
import Drawer from '@material-ui/core/Drawer';
import MUITooltip from '@material-ui/core/Tooltip';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import SearchOutlinedIcon from '@material-ui/icons/SearchOutlined';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import StorefrontOutlinedIcon from '@material-ui/icons/StorefrontOutlined';
import BuildOutlinedIcon from '@material-ui/icons/BuildOutlined';

import { Tag } from 'core/components/tag';
import { NavIllustration } from 'core/components/nav-illustration';
import useSession from 'core/hooks/use-session';
import useStyles from './nav-rail.styles';

// Same destinations and gates as the classic header tabs and the account menu's Tools item.
const buildGroups = (session) =>
  [
    {
      label: 'Work',
      items: [
        session.isLoggedIn && { label: 'Search', to: '/search/all', match: '/search', Icon: SearchOutlinedIcon },
        session.canQuote && { label: 'New quote', to: '/quote', match: '/quote', Icon: AddCircleOutlineIcon }
      ]
    },
    {
      label: 'Resources',
      items: [
        session.showAgencyPortal && {
          label: 'Agency hub',
          to: '/portal/updates',
          match: '/portal',
          Icon: StorefrontOutlinedIcon
        },
        session.canViewTools && {
          label: 'Tools',
          to: '/staff/tools',
          match: '/staff/tools',
          Icon: BuildOutlinedIcon,
          internalTooltip: 'You see this because you have Tools access'
        }
      ]
    }
  ]
    .map((group) => ({ ...group, items: group.items.filter(Boolean) }))
    .filter((group) => group.items.length);

const NavRailContent = observer(({ inDrawer, onNavigate }) => {
  const classes = useStyles();
  const session = useSession();
  const { pathname } = useLocation();
  const theme = useTheme();
  const fullRail = useMediaQuery(`(min-width: ${theme.screens.standard_desktop}px)`, { noSsr: true });
  const iconOnly = !inDrawer && !fullRail;
  const { firstName, lastName, agencyRole } = session.user || {};
  const userName = [firstName, lastName].filter(Boolean).join(' ');

  return (
    <>
      {buildGroups(session).map((group) => (
        <div key={group.label} className={classes.group}>
          <h2 className={classes.groupLabel}>{group.label}</h2>
          {group.items.map(({ label, to, match, Icon, internalTooltip }) => {
            const active = pathname.startsWith(match);
            return (
              <MUITooltip key={to} title={iconOnly ? label : ''} placement="right">
                <Link
                  to={to}
                  className={classNames(classes.item, { [classes.active]: active })}
                  aria-current={active ? 'page' : undefined}
                  onClick={onNavigate}
                >
                  <Icon className={classes.icon} aria-hidden="true" />
                  <span className={classes.label}>{label}</span>
                  {internalTooltip && (
                    <Tag variant="internal" tooltip={internalTooltip} className={classes.tag}>
                      Internal
                    </Tag>
                  )}
                </Link>
              </MUITooltip>
            );
          })}
        </div>
      ))}
      {/* Holds the nav illustration above the user's name. */}
      <div className={classes.illustrationSlot}>
        <NavIllustration />
      </div>
      {userName && (
        <div className={classes.user}>
          <span className={classes.userName}>{userName}</span>
          {session.isAgency && agencyRole && <span className={classes.userRole}>{agencyRole}</span>}
        </div>
      )}
    </>
  );
});

const NavRail = ({ mobileOpen, onMobileClose }) => {
  const classes = useStyles();
  return (
    <>
      <nav aria-label="Main" className={classes.rail}>
        <NavRailContent />
      </nav>
      <Drawer
        open={mobileOpen}
        onClose={onMobileClose}
        anchor="left"
        classes={{ paper: classes.drawerPaper }}
        className={classes.drawer}
      >
        <nav aria-label="Main" className={classes.drawerNav}>
          <NavRailContent inDrawer onNavigate={onMobileClose} />
        </nav>
      </Drawer>
    </>
  );
};

NavRail.propTypes = {
  mobileOpen: PropTypes.bool.isRequired,
  onMobileClose: PropTypes.func.isRequired
};

export default NavRail;
